import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock, Gift } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import { IMAGES, ORARI_CORSI, ORARI_PROMO } from "@/data/site";

const COURSES = [
  {
    title: "Lotta",
    subtitle: "Greco-Romana & Stile Libero",
    image: IMAGES.heroWrestling,
    to: "/lotta",
    internal: true,
    text: "Il cuore storico del club: tecnica, disciplina e rispetto. Corsi per tutte le età, dai baby agli agonisti.",
  },
  {
    title: "Pesistica e Powerlifting",
    subtitle: "Sollevamento pesi, biathlon e distensione su panca",
    image: IMAGES.liftingClose,
    to: "/pesistica",
    internal: true,
    text: "Forza e metodo: pesistica olimpica, powerlifting e preparazione atletica seguita da tecnici qualificati FIPE.",
  },
  {
    title: "BJJ",
    subtitle: "Brazilian Jiu-Jitsu",
    image: IMAGES.bjjNew,
    to: "/bjj",
    internal: true,
    text: "Lotta a terra e sottomissioni: strategia e controllo. Una disciplina che ha già regalato al CAB titoli mondiali e italiani.",
  },
  {
    title: "Grappling",
    subtitle: "Lotta senza gi",
    image: IMAGES.grapplingNew,
    to: "/grappling",
    internal: true,
    text: "Prese, proiezioni e sottomissioni in un allenamento completo e dinamico, per tutti i livelli.",
  },
  {
    title: "Difesa personale",
    subtitle: "M.G.A. FIJLKAM",
    image: IMAGES.difNew,
    to: "/difesa-personale",
    internal: true,
    text: "Metodo Globale Autodifesa FIJLKAM: tecniche pratiche e consapevolezza per la sicurezza personale, adatte a tutti.",
  },
  {
    title: "Ginnastica posturale",
    subtitle: "Mobilità e benessere",
    image: IMAGES.ginNew,
    to: "/ginnastica-posturale",
    internal: true,
    text: "Un percorso dolce per riequilibrare la postura, prevenire i dolori e ritrovare energia e mobilità.",
  },
];

export default function Corsi() {
  return (
    <div data-testid="corsi-page">
      <PageHero
        image={IMAGES.corsiLotta}
        overline="Offerta formativa"
        titleLines={["I nostri", "corsi"]}
        subtitle="Dalle discipline olimpiche alle attività per il benessere: al Club Atletico Bologna c'è un percorso per ogni età e ogni obiettivo."
      />

      <section className="bg-white py-20 md:py-32" data-testid="corsi-grid">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading
            overline="Scegli la tua disciplina"
            title="Sei percorsi, una sola casa"
            subtitle="Tutti i corsi si svolgono nella nostra sede di via Aristotile Fioravanti 22, a Bologna. La prima prova è sempre gratuita."
          />

          <Stagger className="mt-14 grid grid-cols-1 gap-6 md:mt-20 md:grid-cols-2 lg:grid-cols-3">
            {COURSES.map((c) => (
              <StaggerItem key={c.title}>
                <Link
                  to={c.to}
                  data-testid={`course-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-ink/10 bg-cream transition-all duration-500 hover:-translate-y-1.5 hover:border-cab/40 hover:shadow-[0_20px_50px_rgba(62,106,158,0.15)]"
                >
                  <div className="relative aspect-[16/11] overflow-hidden">
                    <img
                      src={c.image}
                      alt={c.title}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-cab-deep/75 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-5 right-5">
                      <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-cream">
                        {c.title}
                      </h3>
                      <p className="text-sm font-medium text-cream/80">{c.subtitle}</p>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="flex-1 text-[15px] leading-relaxed text-muted-foreground">{c.text}</p>
                    <span className="mt-6 inline-flex items-center gap-2 font-bold text-cab">
                      {c.internal ? "Scopri di più" : "Info e iscrizioni"}
                      {c.internal ? (
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      ) : (
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      )}
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Orari settimanali */}
      <section className="bg-cream py-20 md:py-32" data-testid="corsi-orari">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading
            overline="Orari settimanali"
            title="Quando ci alleniamo"
            subtitle="Ecco gli orari aggiornati di tutti i corsi in palestra. La prima prova è sempre gratuita e per i bambini sotto i 12 anni offriamo un mese di prova omaggio."
          />

          <Stagger className="mt-14 grid gap-6 md:mt-20 md:grid-cols-2">
            {ORARI_CORSI.map((corso) => (
              <StaggerItem key={corso.id}>
                <div
                  data-testid={`orario-${corso.id}`}
                  className="flex h-full flex-col rounded-2xl border border-ink/10 bg-white p-7 md:p-8"
                >
                  <div className="flex items-start gap-4 border-b border-ink/10 pb-5">
                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cab text-cream">
                      <Clock className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-display text-xl font-extrabold uppercase tracking-tight text-ink md:text-2xl">
                        {corso.title}
                      </h3>
                      {corso.note && (
                        <p className="mt-1 text-xs font-medium uppercase tracking-wider text-cab">{corso.note}</p>
                      )}
                    </div>
                  </div>

                  <ul className="mt-5 space-y-4">
                    {corso.slots.map((s, i) => (
                      <li key={i} className="grid grid-cols-1 gap-1 sm:grid-cols-[1fr_auto] sm:items-baseline sm:gap-4">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.15em] text-cab">{s.group}</p>
                          <p className="mt-0.5 text-[15px] font-medium text-ink">{s.days}</p>
                        </div>
                        <p className="font-display text-lg font-bold tracking-tight text-cab-deep sm:text-right">
                          {s.time}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-col gap-4 rounded-2xl border border-cab/20 bg-cab/5 p-6 sm:flex-row sm:items-center md:mt-14 md:p-8">
              <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cab text-cream">
                <Gift className="h-6 w-6" />
              </span>
              <div>
                <p className="font-display text-sm font-bold uppercase tracking-[0.2em] text-cab">
                  {ORARI_PROMO.eyebrow}
                </p>
                <ul className="mt-2 flex flex-col gap-1 text-[15px] text-ink md:flex-row md:flex-wrap md:gap-x-6">
                  {ORARI_PROMO.items.map((it) => (
                    <li key={it} className="font-medium">{it}</li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
